import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { Plug, CheckCircle2, XCircle, RefreshCw, Loader2, ExternalLink } from 'lucide-react'

interface BokunIntegration {
  vendor_id?: string
  bokun_vendor_id?: string | null
  platform_merchant_id?: string | null
  merchant_id?: number | null
  connected: boolean
  last_sync?: string | null
  sync_status?: 'idle' | 'syncing' | 'success' | 'error'
  products_count?: number
}

export default function IntegrationsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [bokunVendorId, setBokunVendorId] = useState('')
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Fetch integration status
  const { data: integrationData, isLoading: isLoadingIntegration } = useQuery({
    queryKey: ['bokun-integration', vendorId],
    queryFn: async () => {
      if (!vendorId) throw new Error('Vendor ID is required')
      try {
        return await api.integrations.getBokunIntegration(vendorId.toString())
      } catch (error: any) {
        // If 404, return default disconnected state
        if (error?.response?.status === 404) {
          return {
            data: {
              vendor_id: vendorId,
              bokun_vendor_id: null,
              connected: false,
              last_sync: null,
              sync_status: 'idle' as const,
              products_count: 0,
            } as BokunIntegration
          }
        }
        throw error
      }
    },
    enabled: !!vendorId,
  })

  // Axios response structure: { data: { success: true, data: { ... } } }
  const integration = integrationData?.data?.data as BokunIntegration | undefined

  // Initialize form with existing integration
  useEffect(() => {
    if (integration?.platform_merchant_id) {
      setBokunVendorId(integration.platform_merchant_id)
    }
  }, [integration])

  // Connect Bokun integration mutation (using generic endpoint)
  const connectBokunMutation = useMutation({
    mutationFn: async () => {
      if (!vendorId) throw new Error('Vendor ID is required')
      if (!bokunVendorId.trim()) throw new Error('Bokun Vendor ID is required')

      return api.integrations.connectIntegration({
        platform_name: 'Bokun',
        platform_merchant_id: bokunVendorId.trim(),
        vendor_id: typeof vendorId === 'string' ? parseInt(vendorId, 10) : vendorId,
      })
    },
    onSuccess: (response: any) => {
      posthog.capture('provider_connect_completed', { platform: 'bokun' })
      queryClient.invalidateQueries({ queryKey: ['bokun-integration', vendorId] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: response?.data?.data?.message || 'Bokun integration connected successfully!',
        confirmText: 'OK',
      })
      setBokunVendorId('')
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Failed to connect Bokun integration. Please try again.'
      posthog.capture('provider_connect_failed', { platform: 'bokun', error: msg })
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: msg,
        confirmText: 'OK',
      })
    },
  })

  // Sync feeds mutation
  const syncFeedsMutation = useMutation({
    mutationFn: async () => {
      if (!vendorId) throw new Error('Vendor ID is required')
      posthog.capture('feeds_sync_started', { platform: 'bokun' })
      return api.integrations.syncBokunFeeds(vendorId.toString())
    },
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['bokun-integration', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds', vendorId] })
      const data = response?.data?.data || response?.data || {}
      const syncedCount = data.synced_count || data.feeds_synced || 0
      posthog.capture('feeds_sync_completed', { platform: 'bokun', feed_count: syncedCount })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Sync Complete',
        message: `Successfully synced ${syncedCount} feeds from Bokun.`,
        confirmText: 'OK',
      })
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Failed to sync feeds from Bokun. Please try again.'
      posthog.capture('feeds_sync_failed', { platform: 'bokun', error: msg })
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Sync Failed',
        message: msg,
        confirmText: 'OK',
      })
    },
  })

  // Disconnect integration mutation (using generic endpoint)
  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!vendorId) throw new Error('Vendor ID is required')
      return api.integrations.disconnectIntegration(vendorId.toString(), 'bokun')
    },
    onSuccess: () => {
      posthog.capture('provider_disconnected', { platform: 'bokun' })
      queryClient.invalidateQueries({ queryKey: ['bokun-integration', vendorId] })
      setBokunVendorId('')
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Disconnected',
        message: 'Bokun integration has been disconnected.',
        confirmText: 'OK',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || error?.message || 'Failed to disconnect integration.',
        confirmText: 'OK',
      })
    },
  })

  const handleConnect = () => {
    if (!bokunVendorId.trim()) {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Validation Error',
        message: 'Please enter your Bokun Vendor ID.',
        confirmText: 'OK',
      })
      return
    }
    if (!vendorId) {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Vendor ID is required',
        confirmText: 'OK',
      })
      return
    }
    posthog.capture('provider_connect_started', { platform: 'bokun', bokun_vendor_id: bokunVendorId.trim() })
    connectBokunMutation.mutate()
  }

  const handleDisconnect = () => {
    setAlertModal({
      isOpen: true,
      type: 'warning',
      title: 'Disconnect Integration',
      message: 'Are you sure you want to disconnect your Bokun integration? This will stop automatic product syncing.',
      onConfirm: () => disconnectMutation.mutate(),
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
    })
  }

  const handleSync = () => {
    syncFeedsMutation.mutate()
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'var(--color-sky-blue)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}>
            <Plug size={24} />
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: 600,
              color: 'var(--color-gray-900)',
            }}>
              Integrations
            </h1>
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.875rem',
              color: 'var(--color-gray-600)',
            }}>
              Connect and sync your products from external platforms
            </p>
          </div>
        </div>
      </div>

      {isLoadingIntegration ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-sky-blue)' }} />
          <p style={{ marginTop: '1rem', color: 'var(--color-gray-600)' }}>Loading integration status...</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Bokun Integration Card */}
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid var(--color-gray-300)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Plug size={28} color="var(--color-gray-700)" />
                </div>
                <div>
                  <h2 style={{
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--color-gray-900)',
                    marginBottom: '0.25rem',
                  }}>
                    Bokun
                  </h2>
                  <p style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    color: 'var(--color-gray-600)',
                  }}>
                    Sync your tours and activities from Bokun
                  </p>
                </div>
              </div>
              {integration?.connected ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '6px',
                  backgroundColor: '#d1fae5',
                  color: '#065f46',
                }}>
                  <CheckCircle2 size={16} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Connected</span>
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-gray-100)',
                  color: 'var(--color-gray-600)',
                }}>
                  <XCircle size={16} />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Not Connected</span>
                </div>
              )}
            </div>

            {!integration?.connected ? (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    color: 'var(--color-gray-900)',
                  }}>
                    Bokun Vendor ID *
                  </label>
                  <input
                    type="text"
                    value={bokunVendorId}
                    onChange={(e) => setBokunVendorId(e.target.value)}
                    placeholder="Enter your Bokun Vendor ID"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid var(--color-gray-300)',
                      fontSize: '0.875rem',
                    }}
                  />
                  <p style={{
                    marginTop: '0.5rem',
                    fontSize: '0.75rem',
                    color: 'var(--color-gray-600)',
                  }}>
                    You can find your Vendor ID in your Bokun account settings
                  </p>
                </div>
                <button
                  onClick={handleConnect}
                  disabled={connectBokunMutation.isPending}
                  style={{
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--color-sky-blue)',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: connectBokunMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: connectBokunMutation.isPending ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {connectBokunMutation.isPending ? (
                    <>
                      <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Plug size={16} />
                      Connect Bokun
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'var(--color-gray-50)',
                  borderRadius: '8px',
                }}>
                  <div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: 'var(--color-gray-600)',
                      marginBottom: '0.25rem',
                    }}>
                      Vendor ID
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'var(--color-gray-900)',
                    }}>
                      {integration.platform_merchant_id}
                    </p>
                  </div>
                  <div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: 'var(--color-gray-600)',
                      marginBottom: '0.25rem',
                    }}>
                      Feeds Synced
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'var(--color-gray-900)',
                    }}>
                      {integration.products_count || 0}
                    </p>
                  </div>
                  <div>
                    <p style={{
                      margin: 0,
                      fontSize: '0.75rem',
                      color: 'var(--color-gray-600)',
                      marginBottom: '0.25rem',
                    }}>
                      Last Sync
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'var(--color-gray-900)',
                    }}>
                      {integration.last_sync
                        ? new Date(integration.last_sync).toLocaleString()
                        : 'Never'
                      }
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleSync}
                    disabled={syncFeedsMutation.isPending}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: 'var(--color-sky-blue)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: syncFeedsMutation.isPending ? 'not-allowed' : 'pointer',
                      opacity: syncFeedsMutation.isPending ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {syncFeedsMutation.isPending ? (
                      <>
                        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={16} />
                        Sync Feeds
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnectMutation.isPending}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: disconnectMutation.isPending ? 'not-allowed' : 'pointer',
                      opacity: disconnectMutation.isPending ? 0.6 : 1,
                    }}
                  >
                    {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                  <a
                    href="https://www.bokun.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '1px solid var(--color-gray-300)',
                      backgroundColor: 'white',
                      color: 'var(--color-gray-700)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <ExternalLink size={16} />
                    Visit Bokun
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Info Section - Only show when not connected */}
          {!integration?.connected && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: 'var(--color-gray-50)',
              borderRadius: '12px',
              border: '1px solid var(--color-gray-200)',
            }}>
              <h3 style={{
                margin: 0,
                marginBottom: '0.75rem',
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--color-gray-900)',
              }}>
                How it works
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '1.5rem',
                color: 'var(--color-gray-700)',
                fontSize: '0.875rem',
                lineHeight: '1.75',
              }}>
                <li>Enter your Bokun Vendor ID to connect your account</li>
                <li>Click "Sync Feeds" to import your tours and activities</li>
                <li>Feeds will be automatically created in your inventory</li>
                <li>You can sync again anytime to update your feeds</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({
          isOpen: false,
          type: 'info',
          title: '',
          message: ''
        })}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}

